<template>
  <span class="tabular-nums">{{ formatted }}</span>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onBeforeUnmount } from "vue";

// Counts up from the previous value to the new one (ease-out, ~700ms).
// Respects prefers-reduced-motion. Formats with French digit grouping.
export default defineComponent({
  name: "AnimatedNumber",
  props: {
    value: { type: [Number, String], default: 0 },
    decimals: { type: Number, default: 0 },
    duration: { type: Number, default: 700 },
  },
  setup(props) {
    const display = ref(0);
    let frame = 0;

    const target = computed(() => Number(props.value) || 0);
    const formatter = computed(
      () =>
        new Intl.NumberFormat("fr-FR", {
          minimumFractionDigits: props.decimals,
          maximumFractionDigits: props.decimals,
        })
    );
    const formatted = computed(() => formatter.value.format(display.value));

    function animate(from: number, to: number) {
      cancelAnimationFrame(frame);
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (reduce || from === to) {
        display.value = to;
        return;
      }
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / props.duration);
        const eased = 1 - Math.pow(1 - t, 3);
        display.value = from + (to - from) * eased;
        if (t < 1) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    }

    watch(target, (to, from) => animate(from ?? 0, to), { immediate: true });
    onBeforeUnmount(() => cancelAnimationFrame(frame));

    return { formatted };
  },
});
</script>
